import React from 'react';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { AlertTriangle, RefreshCw, Home, ArrowLeft } from 'lucide-react';

type State = { 
  hasError: boolean; 
  errorId: string | null;
  errorMessage: string;
};

export class AppErrorBoundary extends React.Component<React.PropsWithChildren, State> {
  state: State = { 
    hasError: false, 
    errorId: null,
    errorMessage: 'An unexpected error occurred'
  };

  static getDerivedStateFromError(error: Error): State {
    // Generate a unique error ID for tracking
    const errorId = `ERR-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 5)}`.toUpperCase();
    
    return { 
      hasError: true, 
      errorId,
      errorMessage: error.message || 'An unexpected error occurred'
    };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log error details for debugging
    const errorDetails = {
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
      errorId: this.state.errorId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // Log to console for development
    console.group('🚨 Application Error Caught by Boundary');
    console.error('Error:', error);
    console.error('Error Info:', info);
    console.error('Error Details:', errorDetails);
    console.groupEnd();

    // TODO: Send to error reporting service (Sentry, etc.)
    // This would be integrated when SENTRY_DSN is configured
    try {
      // Store error in localStorage for potential reporting
      const storedErrors = JSON.parse(localStorage.getItem('app_errors') || '[]');
      storedErrors.push(errorDetails);
      // Keep only last 5 errors to prevent storage bloat
      localStorage.setItem('app_errors', JSON.stringify(storedErrors.slice(-5)));
    } catch (e) {
      // Ignore localStorage errors
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, errorId: null, errorMessage: 'An unexpected error occurred' });
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleGoBack = () => {
    window.history.back();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <Card className="max-w-lg w-full">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-error-100 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 text-error-600" />
              </div>
              <CardTitle className="text-xl">Oops! Something went wrong</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert variant="error">
                <AlertTriangle className="w-4 h-4" />
                <div>
                  <p className="font-medium">We encountered an unexpected error</p>
                  <p className="text-sm mt-1">
                    Don't worry - this has been logged and we'll look into it. 
                    You can try refreshing the page or going back to continue using the app.
                  </p>
                </div>
              </Alert>

              {this.state.errorId && (
                <div className="bg-gray-100 rounded-md p-3 text-sm">
                  <p className="text-gray-600">
                    <strong>Error ID:</strong> {this.state.errorId}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    Please include this ID if you contact support.
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-2 pt-2">
                <Button 
                  onClick={this.handleRetry}
                  leftIcon={<RefreshCw className="w-4 h-4" />}
                  className="w-full"
                >
                  Refresh Page
                </Button>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    onClick={this.handleGoBack}
                    leftIcon={<ArrowLeft className="w-4 h-4" />}
                    className="flex-1"
                  >
                    Go Back
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={this.handleGoHome}
                    leftIcon={<Home className="w-4 h-4" />}
                    className="flex-1"
                  >
                    Go Home
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}



